import React, { useEffect } from 'react';
import { Button, Card, Grid, IconButton, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import AddIcon from '@mui/icons-material/Add';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import { getUsers, handleToggleAddNewUserDialog, handleToggleChangeRoleDialog, handleToggleEnableUserDialog } from '../../store/usermanagement-slice/usermanagement-slice';
import Loader from '../common/Loader';
import { Header } from '../common/Header';
import { ChangeRoleDialog } from './change-role-dialog';
import { EnableUserDialog } from './enable-user-dialog';
import { AddNewUserDialog } from './add-new-user-dialog';
import moment from 'moment';

export interface User {
  userID: number,
  username: string,
  email: string,
  role: string,
  isEnabled: boolean,
  isConfirmed: boolean,
  isCurrentUser: boolean
}

const columns = (dispatch: any, t : Function) : GridColDef[] => [
  { field: 'userID', headerName: t('USERMANAGEMENT_ID_COLUMN'), width: 100 },
  { field: 'email', headerName: t('USERMANAGEMENT_EMAIL_COLUMN'), width: 350 },
  { field: 'role', headerName: t('USERMANAGEMENT_ROLE_COLUMN'), width: 150 },
  { field: 'isEnabled', headerName: t('USERMANAGEMENT_ENABLED_COLUMN'), width: 150, renderCell: ({value}) => { return value ? t('YES_LABEL') : t('NO_LABEL') }},
  { field: 'isConfirmed', headerName: t('USERMANAGEMENT_CONFIRMED_COLUMN'), width: 150, renderCell: ({value}) => { return value ? t('YES_LABEL') : t('NO_LABEL') } },
  {field: 'subscriptionName', width:150, renderHeader: () => renderHeaderTwoLines(t('USERMANAGEMENT_ACTIVE_SUBSCRIPTION_NAME_COLUMN')), renderCell: ({value}) => { return t(value) }},
  {field: 'subscriptionStart', width: 140, renderHeader: () => renderHeaderTwoLines(t('USERMANAGEMENT_ACTIVE_SUBSCRIPTION_START_DATE_COLUMN')), valueFormatter: (params) => {if (!params.value) return ''; return moment.utc(params.value).local().format('DD/MM/YYYY')}},
  {field: 'subscriptionEnd', width: 140, renderHeader: () => renderHeaderTwoLines(t('USERMANAGEMENT_ACTIVE_SUBSCRIPTION_END_DATE_COLUMN')), valueFormatter: (params) => {if (!params.value) return ''; return moment.utc(params.value).local().format('DD/MM/YYYY')}},
  {field: 'subscriptionModelsAllowed', width: 150, renderHeader: () => renderHeaderTwoLines(t('USERMANAGEMENT_ACTIVE_SUBSCRIPTION_MODELS_ALLOWED_COLUMN')), align: 'right'},
  {field: 'subscriptionModelsTrained', width: 150, renderHeader: () => renderHeaderTwoLines(t('USERMANAGEMENT_ACTIVE_SUBSCRIPTION_MODELS_TRAINED_COLUMN')), align: 'right'},
  { field: '', headerName: t('USERMANAGEMENT_ACTION_COLUMN'), minWidth: 200, flex: 1, renderCell: (params) => {
    return <Grid container display="flex" justifyContent="center" spacing={4}>
      <Grid item>
        <Tooltip title={ params.row.isEnabled ? t('USER_MANAGEMENT_DISABLE_USER') : t('USER_MANAGEMENT_ENABLE_USER')}>
          <IconButton aria-label="enable" onClick={() => dispatch(handleToggleEnableUserDialog({ open: true, body: {
            email: params.row.email,
            enable: !params.row.isEnabled
          }}))}>
            { params.row.isEnabled ? <ToggleOffIcon className='info-icon' /> : <ToggleOnIcon className='info-icon' /> }
          </IconButton>
        </Tooltip>
      </Grid>
      <Grid item>
        <Tooltip title={t('USER_MANAGEMENT_CHANGE_ROLE')}>
          <IconButton aria-label="change" onClick={() => {
            dispatch(handleToggleChangeRoleDialog({ open: true, body: {
              email: params.row.email
            }}))
          }}><ManageAccountsIcon className='download-icon' /></IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  } }
];

export const renderHeaderTwoLines = (translated: string) => {
  const words = translated.split(' ');

  const firstLine = words[0]; 
  const secondLine = words.slice(1).join(' '); 

  return (
    <div style={{lineHeight: 1.2 }}>
      {firstLine}
      {secondLine && <><br />{secondLine}</>}
    </div>
  );
};


export function UserManagement() { 
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { loadingUsers, users, reloadUsers, openLoader } = useAppSelector((state: RootState) => state.usermanagement);

  function handleAddNewUserDialogToggle() 
  {
    dispatch(handleToggleAddNewUserDialog(true));
  }

  useEffect(() => {
    dispatch(getUsers());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadUsers]);

  if(loadingUsers) return <Loader open={loadingUsers} />;

  if (users.length === 0)
    return (
      <Card style={{marginTop: '5px'}}>
        <Header title={t('USER_MANAGEMENT_NO_USERS')}/>
        <Button
          onClick={handleAddNewUserDialogToggle}
          variant="contained"
          startIcon={<AddIcon />}
        >
          {t('USER_MANAGEMENT_ADD_NEW_USER')}
        </Button>
        <AddNewUserDialog />
        <ChangeRoleDialog />
        <EnableUserDialog />
      </Card>
    );

  return (
    <Card style={{marginTop: '5px'}}>
      { openLoader ? <Loader open={openLoader} /> : '' }
      <Header title={t('USER_MANAGEMENT_USERS_LABEL')} />
      <Grid p={1.5} display="flex" justifyContent="flex-end" alignItems="flex-end">
        <Button size="small" onClick={() => {
          handleAddNewUserDialogToggle();
        }} className='button-color' variant="contained" startIcon={<AddIcon />}>{t('USER_MANAGEMENT_ADD_NEW_USER')}</Button>
      </Grid>
      <Box sx={{ overflow: "auto" }} p={1.5}>
        <Box sx={{ width: "100%", display: "table", tableLayout: "fixed" }}>
          <DataGrid
            autoHeight
            rows={users}
            columns={columns(dispatch, t)}
             sx={{
              '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold', },
              '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader': {
                borderRight: '1px solid #E0E0E0',
              },
              '& .MuiDataGrid-cell:last-child, & .MuiDataGrid-columnHeader:last-child': {
                borderRight: 'none',
              },
            }}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            getRowId={(row) => row.userID}
            disableRowSelectionOnClick
            disableColumnSelector
            disableDensitySelector
            componentsProps={{
              pagination: {
                labelRowsPerPage: t('ROWS_PER_PAGE_TEXT')
              }
            }}
            rowHeight={35}
            columnHeaderHeight={40}
          />
        </Box>
      </Box>
      <ChangeRoleDialog />
      <EnableUserDialog />
      <AddNewUserDialog />
    </Card>
  );
}

export default UserManagement;
